/**
 * iter214 — WhatsApp Inbound Messages card (Phase 1).
 *
 * Shows recent INCOMING WhatsApp messages from members. Used in two places:
 *  • WhatsAppSettingsPage (super-admin) — global view via GET /whatsapp/inbound
 *  • GroupDetailPage (group admin) — scoped via GET /whatsapp/groups/{id}/inbound
 *
 * Each row has a "Mark handled" toggle so admins can triage. Button replies
 * are highlighted differently from plain text. Auto-refreshes on a timer.
 *
 * Props:
 *   apiPath:         backend path to fetch (e.g. '/whatsapp/inbound' or
 *                    `/whatsapp/groups/${groupId}/inbound`)
 *   title:           card heading
 *   refreshInterval: ms (default 30_000)
 *   defaultLimit:    rows to fetch (default 50)
 */
import { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { RefreshCw, Loader2, Inbox, MessageCircle, MousePointerClick, CheckCircle2, Circle } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

function relativeTime(iso) {
  if (!iso) return '';
  const ms = Date.now() - new Date(iso).getTime();
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export const WhatsAppInboundLogCard = ({
  apiPath,
  title = 'Inbound WhatsApp Messages',
  refreshInterval = 30000,
  defaultLimit = 50,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [phoneFilter, setPhoneFilter] = useState('');
  const [showHandled, setShowHandled] = useState(false); // false → only un-handled
  const [updatingId, setUpdatingId] = useState(null);

  const fetchInbound = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: String(defaultLimit) });
      if (phoneFilter.trim()) params.set('phone', phoneFilter.trim());
      if (!showHandled) params.set('handled', 'false');
      const res = await api.get(`${apiPath}?${params.toString()}`);
      setRows(res.data?.entries || []);
    } catch (e) {
      // Soft-fail: keep last data
    } finally {
      setLoading(false);
    }
  }, [apiPath, defaultLimit, phoneFilter, showHandled]);

  useEffect(() => {
    fetchInbound();
    const t = setInterval(fetchInbound, refreshInterval);
    return () => clearInterval(t);
  }, [fetchInbound, refreshInterval]);

  const toggleHandled = async (row) => {
    setUpdatingId(row.id);
    try {
      const res = await api.post(`/whatsapp/inbound/${row.id}/handled`, {
        handled: !row.handled,
      });
      const updated = { ...row, ...res.data };
      setRows((prev) => prev.map((r) => (r.id === row.id ? updated : r)));
      toast.success(updated.handled ? 'Marked handled' : 'Re-opened');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setUpdatingId(null);
    }
  };

  const unhandledCount = rows.filter((r) => !r.handled).length;

  return (
    <Card className="p-4 sm:p-5 rounded-2xl border-2 space-y-3" data-testid="whatsapp-inbound-log-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 min-w-0">
          <Inbox className="w-4 h-4 text-primary shrink-0" />
          <h3 className="text-sm font-bold uppercase tracking-wide truncate">{title}</h3>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={fetchInbound}
          disabled={loading}
          data-testid="inbound-refresh-btn"
          className="rounded-full h-8 text-xs"
        >
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1">Refresh</span>
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Input
          type="search"
          placeholder="Filter by phone…"
          value={phoneFilter}
          onChange={(e) => setPhoneFilter(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') fetchInbound(); }}
          data-testid="inbound-phone-filter-input"
          className="h-9 rounded-lg border-2 text-xs flex-1 min-w-[140px]"
        />
        <Button
          size="sm"
          variant={showHandled ? 'outline' : 'default'}
          onClick={() => setShowHandled(!showHandled)}
          data-testid="inbound-handled-toggle"
          className="rounded-full h-9 text-xs"
        >
          {showHandled ? 'Showing all' : `Open only (${unhandledCount})`}
        </Button>
      </div>

      <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="inbound-rows-container">
        {rows.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground text-xs" data-testid="inbound-empty-state">
            <MessageCircle className="w-6 h-6 mx-auto opacity-30 mb-2" />
            <p>No {showHandled ? '' : 'open '}inbound messages yet.</p>
            <p className="text-xs mt-1">Members can reply to any WhatsApp you send — replies will appear here.</p>
          </div>
        ) : (
          rows.map((row) => {
            const isButton = row.type === 'button';
            const senderLabel = row.from_user_name || `+${row.from_phone}`;
            const phoneTail = (row.from_phone || '').slice(-4);
            return (
              <div
                key={row.id}
                className={`rounded-xl border p-2.5 space-y-1.5 transition-opacity ${row.handled ? 'opacity-60 bg-muted/20' : 'bg-card'}`}
                data-testid={`inbound-row-${row.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-1.5 min-w-0">
                    {isButton ? (
                      <MousePointerClick className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    ) : (
                      <MessageCircle className="w-3.5 h-3.5 text-primary shrink-0" />
                    )}
                    <p className="text-xs font-semibold truncate">
                      {senderLabel}
                      <span className="text-muted-foreground font-mono text-xs ml-1">··{phoneTail}</span>
                    </p>
                  </div>
                  <span className="text-xs text-muted-foreground shrink-0">
                    {relativeTime(row.received_at)}
                  </span>
                </div>

                {isButton ? (
                  <div className="rounded-md bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-300/40 dark:border-emerald-500/30 px-2 py-1.5 text-xs">
                    <span className="font-bold text-emerald-700 dark:text-emerald-300">{row.button_text}</span>
                    {row.context_template_name && (
                      <span className="text-xs text-muted-foreground ml-1.5">
                        ↳ <code className="font-mono">{row.context_template_name}</code>
                      </span>
                    )}
                    {row.button_payload && (
                      <p className="text-xs text-muted-foreground font-mono mt-0.5">
                        payload: {row.button_payload}
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-xs leading-relaxed bg-muted/30 rounded-md px-2 py-1.5 break-words">
                    {row.text || `[${row.type}]`}
                  </p>
                )}

                {row.context_notif_type && !isButton && (
                  <p className="text-xs text-muted-foreground">
                    Replies to: <code className="font-mono">{row.context_notif_type}</code>
                  </p>
                )}

                <div className="flex items-center justify-between gap-2 pt-0.5">
                  <span className="text-xs text-muted-foreground">
                    {row.routed_to_admin_ids?.length > 0
                      ? `Notified ${row.routed_to_admin_ids.length} admin${row.routed_to_admin_ids.length === 1 ? '' : 's'}`
                      : 'Not routed'}
                  </span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => toggleHandled(row)}
                    disabled={updatingId === row.id}
                    data-testid={`inbound-toggle-handled-${row.id}`}
                    className="h-7 text-xs rounded-full px-2"
                  >
                    {updatingId === row.id ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : row.handled ? (
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    ) : (
                      <Circle className="w-3 h-3" />
                    )}
                    <span className="ml-1">{row.handled ? 'Handled' : 'Mark handled'}</span>
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
};

export default WhatsAppInboundLogCard;
